import { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Chip, Divider } from "@heroui/react";
import { IconCalendarPause, IconGift } from "@tabler/icons-react";
import { api } from "@/lib/api";
import Loader from "@/components/loader";
import { useLocale, useTranslations } from "../../../../i18n";
import { formatDateWithLocale } from "@/utils/format-date-with-locale";

function Empty({ text }: { text: string }) {
  return <div className="text-sm text-gray-400 text-center py-3">{text}</div>;
}

export default function WidgetPassesPanel({ isActive }: { isActive: boolean }) {
  const t = useTranslations("profile.subscriptions");
  const locale = useLocale();
  const language = locale.split("-")[0];
  const [passes, setPasses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isActive) return;

    const load = async () => {
      try {
        setLoading(true);
        const data = await api("/api/widget/profile/passes");
        setPasses(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isActive]);

  const { activePasses, expiredPasses } = useMemo(() => {
    const byDateDesc = (a: any, b: any) =>
      new Date(b.expiryDate).getTime() - new Date(a.expiryDate).getTime();

    const active: any[] = [];
    const expired: any[] = [];

    passes.forEach((pass) => {
      if (pass.status === "ACTIVE") {
        active.push(pass);
      } else {
        expired.push(pass);
      }
    });

    return {
      activePasses: active.sort(byDateDesc),
      expiredPasses: expired.sort(byDateDesc),
    };
  }, [passes]);

  const formatDate = (date?: string | null) => {
    if (!date) {
      return "N/A";
    }

    return formatDateWithLocale(date, "d MMM yyyy", language);
  };

  const renderPassCard = (pass: any) => {
    const hasExtensions =
      pass.holidayExtensions && pass.holidayExtensions.length > 0;
    const extensionDays = hasExtensions
      ? pass.holidayExtensions.reduce((sum, e) => sum + e.daysFrozen, 0)
      : 0;

    /* const statusLabels: Record<string, string> = {
      ACTIVE: t("status.active"),
      EXPIRED: t("status.expired"),
      FROZEN: t("status.frozen"),
    }; */
    // const statusLabel = statusLabels[pass.status] ?? pass.status;

    return (
      <Card key={pass.id} className="p-4">
        <CardBody>
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">
                  {pass.passType?.name}
                </h3>

                {hasExtensions && (
                  <Chip
                    size="sm"
                    color="primary"
                    variant="flat"
                    startContent={<IconGift size={14} />}
                  >
                    {t("extended")}
                  </Chip>
                )}
              </div>

              <p className="text-sm text-gray-500 mt-1">
                {t("validUntil", { date: formatDate(pass.expiryDate) })}
              </p>
            </div>

            {/* <Chip
              size="sm"
              color={pass.status === "ACTIVE" ? "success" : "warning"}
              variant="bordered"
            >
              {statusLabel}
            </Chip> */}
          </div>

          <Divider className="my-3" />

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">{t("classes")}</p>
              <p className="font-medium">
                {pass.classesLeft === null
                  ? t("unlimited")
                  : `${pass.classesLeft} / ${pass.passType?.classes}`}
              </p>
            </div>

            <div>
              <p className="text-gray-500">{t("price")}</p>
              <p className="font-medium">
                {Number(pass.passType?.price ?? 0).toFixed(2)} CHF
              </p>
            </div>
          </div>

          {hasExtensions && (
            <div className="mt-4 p-3 rounded-lg bg-green-50 border border-green-200">
              <div className="flex items-start gap-2">
                <IconCalendarPause
                  size={16}
                  className="text-green-600 mt-0.5"
                />
                <div className="text-sm text-green-800">
                  <p className="font-medium">
                    {t("passExtendedDueToHolidays")}
                  </p>
                  <p>{t("daysAddedCount", { count: extensionDays })}</p>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <Loader />
      </div>
    );
  }

  if (activePasses.length === 0 && expiredPasses.length === 0) {
    return <Empty text={t("noActivePasses")} />;
  }

  return (
    <div className="space-y-4">
      {activePasses.map(renderPassCard)}
      {expiredPasses.map(renderPassCard)}
    </div>
  );
}
